import { el, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import { TIERS } from "../game/tiers";
import { progress } from "../store/progress";
import { tierSwatch } from "../render/renderer";

/**
 * Collection / encyclopedia (idea #9): every celestial tier the player has
 * reached gets an entry with its art, name, and a one-line cosmic science
 * blurb — reinforcing the particle→cosmos theme. Undiscovered tiers show as
 * silhouettes. Wild/bomb/boss entries unlock on first lifetime encounter.
 */
export class CollectionScreen implements Screen {
  private off?: () => void;
  constructor(private nav: Nav) {}

  mount(host: HTMLElement): void {
    const screen = el("div", "screen collection");
    const top = buildTopbar({
      title: "menu.collection",
      onBack: () => this.nav.home(),
      showStardust: true,
    });

    const body = el("div", "collection-body");

    const render = (): void => {
      top.refresh();
      body.innerHTML = "";
      const p = progress.snapshot;

      const discoveredTiers = TIERS.filter((tr) => tr.level <= p.maxTierReached).length;
      const specials: Array<{ key: string; level: number; seen: boolean }> = [
        { key: "wild", level: -1, seen: p.seenWild },
        { key: "bomb", level: -2, seen: p.seenBomb },
        { key: "boss", level: -103, seen: p.seenBoss },
      ];
      const discovered = discoveredTiers + specials.filter((s) => s.seen).length;
      const total = TIERS.length + specials.length;

      const summary = el("div", "collection-summary");
      summary.textContent = t("collection.progress", { n: discovered, total });
      body.appendChild(summary);

      const list = el("div", "collection-list");

      for (const tr of TIERS) {
        const seen = tr.level <= p.maxTierReached;
        list.appendChild(this.card(tr.level, `tier.${tr.key}`, `collection.${tr.key}`, seen));
      }
      for (const s of specials) {
        list.appendChild(
          this.card(s.level, `collection.${s.key}.name`, `collection.${s.key}`, s.seen),
        );
      }
      body.appendChild(list);
    };

    screen.append(top.node, body);
    host.appendChild(screen);
    render();
    this.off = i18n.onChange(render);
  }

  private card(level: number, nameKey: string, blurbKey: string, seen: boolean): HTMLElement {
    const card = el("div", `collection-card ${seen ? "seen" : "hidden-entry"}`);
    card.innerHTML = `
      <div class="cc-art">${tierSwatch(level, 56)}</div>
      <div class="cc-info">
        <div class="cc-name">${seen ? t(nameKey) : "???"}</div>
        <div class="cc-blurb">${seen ? t(blurbKey) : t("collection.locked")}</div>
      </div>`;
    return card;
  }

  unmount(): void {
    this.off?.();
  }
}
